import { useMemo } from "react";
import { ApolloError } from "@apollo/client";
import jwtDecode from "jwt-decode";

/**
 * A hook which returns information about the current user.
 * Works for both fake auth (development) and OIDC auth (production).
 */
export default function useCurrentUser(): {
  error: ApolloError | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { id: string } | undefined;
} {
  // Check for token in localStorage (works for both fake auth and OIDC)
  const token = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;

  const isAuthenticated = useMemo(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode<{ exp: number; userId?: string; id?: string }>(token);
        const now = new Date();
        const isValid = decodedToken.exp * 1000 > now.getTime();
        return isValid;
      } catch (error) {
        console.error("Invalid token:", error);
        // Clear invalid token
        if (typeof window !== "undefined") {
          window.localStorage.removeItem("token");
        }
        return false;
      }
    }
    return false;
  }, [token]);

  const user = useMemo(() => {
    if (isAuthenticated && token) {
      try {
        const decodedToken = jwtDecode<{ userId?: string; id?: string; sub?: string }>(token);
        const userId = decodedToken.userId || decodedToken.id || decodedToken.sub;
        return userId ? { id: userId } : undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }, [isAuthenticated, token]);

  return {
    error: undefined,
    isAuthenticated,
    isLoading: false,
    user,
  };
}
